import { Avatar, Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import React from "react";
import { HeaderToolbar, Root } from "styled/layout-components/header";
import { logout, selectKeycloak } from "features/auth-slice";
import { useAppDispatch, useAppSelector } from "app/hooks";
import strings from "localization/strings";
import LogoutIcon from "@mui/icons-material/Logout";
import { selectLocale, setLocale } from "features/locale-slice";
import { HelpOutline } from "@mui/icons-material";
import Config from "app/config";
import theme from "theme";
import { useLocation } from "react-router-dom";
import LocalizationUtils from "utils/localization-utils";
import NavigationUtils from "utils/navigation-utils";

/**
 * Header component
 *
 * @param props component properties
 */
const Header: React.FC = ({
  children
}) => {
  const keycloak = useAppSelector(selectKeycloak);
  const { locale } = useAppSelector(selectLocale);
  const { pathname } = useLocation();
  const currentNavigation = NavigationUtils.matchNavigation(pathname);
  const [ title, description ] = LocalizationUtils.getLocalizedNavigationLink(currentNavigation);

  const dispatch = useAppDispatch();
  const userEmail = keycloak?.tokenParsed?.email || "";
  const userInitials = userEmail.slice(0, 2).toUpperCase() || "GF";
  let userRole = "Form manager";
  if (keycloak?.hasRealmRole("metatavu-admin")) {
    userRole = "Metatavu administrator";
  } else if (keycloak?.hasRealmRole("system-admin")) {
    userRole = "System administrator";
  }
  
  /**
   * Renders info button with link to instructions of essote management portal
   */
  const renderInfoButton = () => {
    const tutorialUrl = Config.getTutorialUrl() || "";

    if (!tutorialUrl) {
      return null;
    }

    return (
      <Tooltip title={ strings.header.infoLabel } placement="left" arrow>
        <IconButton href={ tutorialUrl } target="_blank">
          <HelpOutline
            fontSize="large"
            sx={{ color: theme.palette.primary.main }}
          />
        </IconButton>
      </Tooltip>
    );
  };
  return (
    <Root
      position="static"
      sx={{ backgroundColor: theme.header.background }}
    >
      <HeaderToolbar>
        <Stack spacing={ 0 }>
          <Typography
            sx={{
              fontSize: 17, fontWeight: 500, lineHeight: "20px"
            }}
          >
            { title }
          </Typography>
          <Typography
            sx={{
              color: "#8a8f8c", fontSize: 12, lineHeight: "18px"
            }}
          >
            { description }
          </Typography>
        </Stack>
        <Stack
          direction="row"
          spacing={ 2 }
          sx={{
            alignSelf: { xs: "stretch", sm: "auto" },
            justifyContent: { xs: "space-between", sm: "flex-start" },
            minWidth: 0
          }}
        >
          { renderInfoButton() }
          <Box
            aria-label={ strings.header.selectLanguage }
            sx={{
              display: "flex",
              overflow: "hidden",
              border: "1px solid #d9dedb",
              borderRadius: 1
            }}
          >
            { strings.getAvailableLanguages().map(language => (
              <Box
                key={ language }
                component="button"
                type="button"
                onClick={ () => dispatch(setLocale(language)) }
                sx={{
                  minWidth: 0,
                  border: 0,
                  px: 1.5,
                  py: 0.75,
                  cursor: "pointer",
                  font: "inherit",
                  fontSize: 13,
                  fontWeight: 500,
                  lineHeight: "15px",
                  color: locale === language ? "#fff" : "#4d4d4d",
                  backgroundColor: locale === language ? "#278062" : "#fff"
                }}
              >
                { language.toUpperCase() }
              </Box>
            )) }
          </Box>
          <Stack direction="row" spacing={ 1 } alignItems="center" minWidth={ 0 }>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: "#e4efe9",
                color: "#1c5f49",
                fontSize: 13,
                fontWeight: 500
              }}
            >
              { userInitials }
            </Avatar>
            <Box
              sx={{
                minWidth: 0,
                display: { xs: "none", sm: "block" }
              }}
            >
              <Typography
                noWrap
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  maxWidth: 220
                }}
              >
                { userEmail }
              </Typography>
              <Typography noWrap sx={{ color: "#8a8f8c", fontSize: 12 }}>
                { userRole }
              </Typography>
            </Box>
            <Tooltip title={ strings.generic.logout }>
              <IconButton aria-label={ strings.generic.logout } size="small" onClick={ () => dispatch(logout()) }>
                <LogoutIcon
                  fontSize="small"
                  sx={{ color: "#4d4d4d" }}
                />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </HeaderToolbar>
      { children }
    </Root>
  );
};

export default Header;